import React, { FC, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { auth } from 'src/config/firebase';
import { sessionStore } from 'src/store/sessionStore';
import {
  TrainerPackage,
  PackagePresetTier,
  PACKAGE_PRESET_TEMPLATES,
  DEFAULT_PACKAGE_TEMPLATE,
  validatePackageClient,
  validatePackageDraftClient,
  packagesService,
} from 'src/api/services/packagesService';
import {
  useGetTrainerPackages,
  trainerPackagesQueryKey,
} from 'src/api/queries/useGetTrainerPackages';
import { useSaveTrainerPackage } from 'src/api/mutations/useSaveTrainerPackage';
import { useUnpublishTrainerPackage } from 'src/api/mutations/useUnpublishTrainerPackage';
import { EmptyPackagesView } from './EmptyPackagesView';
import { PackagesListView } from './PackagesListView';
import { PackageForm } from './PackageForm';
import { UnpublishPackageModal } from './UnpublishPackageModal';
import { DocumentTemplateView, TemplateType } from './DocumentTemplateView';
import toast from 'src/utils/appAlert';
import './Packages.scss';

export const Packages: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { currentUser } = sessionStore();
  const trainerId = currentUser?.uid || auth.currentUser?.uid || '';

  // React Query Hooks
  const { data: packages = [], isLoading } = useGetTrainerPackages(trainerId);
  const saveMutation = useSaveTrainerPackage();
  const unpublishMutation = useUnpublishTrainerPackage();
  const [isManualSaving, setIsManualSaving] = useState(false);

  // Navigation Sub-views: 'list' | 'create' | 'edit' | 'template'
  const currentView = searchParams.get('view') || 'list';
  const selectedPackageId = searchParams.get('id');
  const templateTypeParam = (searchParams.get('type') as TemplateType) || 'contractual_terms';

  // Modal State
  const [isUnpublishModalOpen, setIsUnpublishModalOpen] = useState(false);

  // Find currently editing package
  const editingPackage = useMemo(() => {
    if (!selectedPackageId) return packages[0] || null;
    return (
      packages.find((p) => {
        if (p.id === selectedPackageId) return true;
        const lowerSelected = selectedPackageId.toLowerCase();
        if (p.presetTier.toLowerCase() === lowerSelected) return true;
        if (p.presetTier.toLowerCase() === `package ${lowerSelected}`) return true;
        const templateMatch = PACKAGE_PRESET_TEMPLATES.find(
          (t) =>
            t.tier.toLowerCase() === lowerSelected ||
            t.id.toLowerCase() === lowerSelected ||
            t.templateId.toLowerCase() === lowerSelected ||
            lowerSelected === t.tier.replace('Package ', '').toLowerCase(),
        );
        return Boolean(templateMatch && p.presetTier === templateMatch.tier);
      }) || null
    );
  }, [packages, selectedPackageId]);

  // Determine current tier from editingPackage or selectedPackageId
  const currentEditingTier: PackagePresetTier = useMemo(() => {
    if (editingPackage?.presetTier) return editingPackage.presetTier;
    if (selectedPackageId) {
      const lower = selectedPackageId.toLowerCase();
      const matchedTemplate = PACKAGE_PRESET_TEMPLATES.find(
        (t) =>
          t.tier.toLowerCase() === lower ||
          t.id.toLowerCase() === lower ||
          t.templateId.toLowerCase() === lower ||
          lower === t.tier.replace('Package ', '').toLowerCase(),
      );
      if (matchedTemplate) return matchedTemplate.tier;
    }
    return DEFAULT_PACKAGE_TEMPLATE.tier;
  }, [editingPackage, selectedPackageId]);

  const handleTierChange = (newTier: PackagePresetTier, targetPackageId?: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'packages');
      next.set('view', 'edit');
      if (targetPackageId) {
        next.set('id', targetPackageId);
      } else {
        next.set('id', newTier);
      }
      return next;
    });
  };

  // Track draft state when temporarily navigating to template view
  const [createDraft, setCreateDraft] = useState<Partial<TrainerPackage> | null>(null);

  const fromView = searchParams.get('from');
  const fromId = searchParams.get('fromId');

  // View Navigation Handlers
  const navigateTo = (
    view: 'list' | 'create' | 'edit' | 'template',
    extraParams?: Record<string, string>,
  ) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'packages');
      if (view === 'list') {
        next.delete('view');
        next.delete('id');
        next.delete('type');
        next.delete('from');
        next.delete('fromId');
      } else {
        next.set('view', view);
        if (view !== 'template') {
          next.delete('type');
          next.delete('from');
          next.delete('fromId');
        }
        if (extraParams) {
          Object.entries(extraParams).forEach(([k, v]) => next.set(k, v));
        }
      }
      return next;
    });
  };

  const handleCreatePackage = () => {
    if (packages.length >= 3) {
      toast.error('You can create a maximum of 3 packages.');
      return;
    }
    try {
      localStorage.removeItem(`mini_pt_package_create_draft_${trainerId}`);
    } catch {
      // ignore
    }
    setCreateDraft(null);
    navigateTo('create');
  };

  const handleEditPackage = (pkg: TrainerPackage) => {
    navigateTo('edit', { id: pkg.id });
  };

  const handleBackToList = () => {
    setCreateDraft(null);
    navigateTo('list');
  };

  const handleOpenTemplate = (type: TemplateType, currentDraft?: Partial<TrainerPackage>) => {
    if (currentView === 'create' && currentDraft) {
      setCreateDraft(currentDraft);
    }
    const extraParams: Record<string, string> = {
      type,
      from: currentView,
    };
    if (selectedPackageId) {
      extraParams.fromId = selectedPackageId;
    }
    navigateTo('template', extraParams);
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
    const dashboardMain = document.querySelector('.dashboard-main');
    if (dashboardMain) {
      dashboardMain.scrollTop = 0;
    }
  };

  const handleBackFromTemplate = () => {
    if (fromView === 'create') {
      navigateTo('create');
    } else if (fromView === 'edit') {
      navigateTo('edit', fromId ? { id: fromId } : undefined);
    } else {
      navigateTo('list');
    }
  };

  // Save Handler
  const handleSavePackage = async (packageData: Partial<TrainerPackage>) => {
    if (!trainerId) {
      toast.error('Trainer session not found. Please log in again.');
      return;
    }

    setIsManualSaving(true);
    try {
      await saveMutation.mutateAsync({
        trainerId,
        packageData: {
          ...packageData,
          status: 'published',
        },
      });

      try {
        localStorage.removeItem(`mini_pt_package_create_draft_${trainerId}`);
      } catch {
        // ignore
      }
      setCreateDraft(null);

      toast.success(
        currentView === 'create'
          ? 'Package created and published successfully!'
          : 'Package changes published successfully!',
      );
      handleBackToList();
    } catch (err: unknown) {
      console.error('Failed to save package:', err);
      const msg = err instanceof Error ? err.message : 'Failed to save package. Please try again.';
      toast.error(msg);
    } finally {
      setIsManualSaving(false);
    }
  };

  // Direct API Auto-save for package drafts
  const handleAutoSavePackage = async (
    packageData: Partial<TrainerPackage>,
    signal?: AbortSignal,
  ): Promise<TrainerPackage | void> => {
    if (!trainerId) return;
    if (signal?.aborted) return;

    const isDraft = (packageData.status ?? 'draft') === 'draft';
    if (isDraft) {
      const draftValidation = validatePackageDraftClient(packageData);
      if (!draftValidation.isValid) return;
    } else {
      const clientValidation = validatePackageClient(packageData);
      if (!clientValidation.isValid) return;
    }

    try {
      const saved = await packagesService.saveTrainerPackage(
        trainerId,
        {
          ...packageData,
          status:
            packageData.status ||
            (currentView === 'edit' ? editingPackage?.status || 'published' : 'draft'),
        },
        signal,
      );

      if (signal?.aborted) return;

      // Hydrate query cache silently so navigating back shows fresh data immediately
      const qKey = trainerPackagesQueryKey(trainerId);
      queryClient.setQueryData<TrainerPackage[]>(qKey, (oldList = []) => {
        const exists = oldList.some((p) => p.id === saved.id || p.presetTier === saved.presetTier);
        return exists
          ? oldList.map((p) => (p.id === saved.id || p.presetTier === saved.presetTier ? saved : p))
          : [...oldList, saved];
      });

      return saved;
    } catch (err) {
      if (err instanceof Error && (err.name === 'AbortError' || err.message.includes('aborted'))) {
        return;
      }
      console.warn('Auto-save error:', err);
    }
  };

  // Unpublish Handler
  const handleConfirmUnpublish = async () => {
    if (!trainerId || !selectedPackageId) {
      toast.error('Package information missing.');
      return;
    }

    try {
      await unpublishMutation.mutateAsync({
        trainerId,
        packageId: selectedPackageId,
      });

      toast.success('Package has been unpublished and saved as draft.');
      setIsUnpublishModalOpen(false);
      handleBackToList();
    } catch (err: unknown) {
      console.error('Failed to unpublish package:', err);
      const msg =
        err instanceof Error ? err.message : 'Failed to unpublish package. Please try again.';
      toast.error(msg);
    }
  };

  return (
    <div className="packages-container">
      {/* 1. DOCUMENT TEMPLATE VIEW */}
      {currentView === 'template' && (
        <DocumentTemplateView type={templateTypeParam} onBack={handleBackFromTemplate} />
      )}

      {/* 2. CREATE PACKAGE VIEW */}
      {currentView === 'create' && (
        <PackageForm
          key="create-package"
          mode="create"
          initialData={createDraft}
          allPackages={packages}
          onTierChange={handleTierChange}
          onBack={handleBackToList}
          onSave={handleSavePackage}
          onAutoSave={handleAutoSavePackage}
          onOpenTemplate={handleOpenTemplate}
          isSaving={isManualSaving}
        />
      )}

      {/* 3. EDIT PACKAGE VIEW */}
      {currentView === 'edit' && (
        <>
          {isLoading ? (
            <div className="packages-loading-state">
              <div className="packages-spinner" />
              <p>{'Loading package...'}</p>
            </div>
          ) : (
            <PackageForm
              key={editingPackage?.id || currentEditingTier}
              mode="edit"
              initialData={editingPackage}
              initialTier={currentEditingTier}
              allPackages={packages}
              onTierChange={handleTierChange}
              onBack={handleBackToList}
              onSave={handleSavePackage}
              onAutoSave={handleAutoSavePackage}
              onUnpublishClick={() => setIsUnpublishModalOpen(true)}
              onOpenTemplate={handleOpenTemplate}
              isSaving={isManualSaving}
            />
          )}

          <UnpublishPackageModal
            isOpen={isUnpublishModalOpen}
            onCancel={() => setIsUnpublishModalOpen(false)}
            onConfirm={handleConfirmUnpublish}
            isPending={unpublishMutation.isPending}
          />
        </>
      )}

      {/* 4. LIST VIEW (EMPTY OR PACKAGES LIST) */}
      {currentView === 'list' && (
        <div className="packages-main-list-wrapper">
          {isLoading ? (
            <div className="packages-loading-state">
              <div className="packages-spinner" />
              <p>{'Loading your packages...'}</p>
            </div>
          ) : packages.length === 0 ? (
            <EmptyPackagesView onCreateClick={handleCreatePackage} />
          ) : (
            <PackagesListView packages={packages} onEditPackage={handleEditPackage} />
          )}
        </div>
      )}
    </div>
  );
};
